/**
 * Save orchestration for the Edit Event flow (OI-25024): validate, diff, then
 * PATCH the event and each changed session sequentially so per-session
 * rejections (e.g. capacity below the registered count) map to their cards.
 * Free of React — the page turns the outcome into toasts and navigation.
 */
import {
  SpaceEvent,
  UpdateSessionRequest,
  UpdateSpaceEventRequest,
} from "@src/models/spaces";
import { SpacesApiError } from "@src/services/spacesError";
import {
  EditEventFormState,
  EditFormErrors,
  diffAgainst,
  hasEditErrors,
  validateEditForm,
} from "@src/components/spaces/editEventForm";

export interface SaveApi {
  updateEvent: (request: UpdateSpaceEventRequest) => Promise<unknown>;
  updateSession: (change: {
    sessionId: string;
    request: UpdateSessionRequest;
  }) => Promise<unknown>;
}

export type SaveOutcome =
  | { status: "validation"; errors: EditFormErrors }
  | { status: "no-changes" }
  | { status: "rejected"; rejectedSessions: Record<string, string> }
  | { status: "saved" };

export const saveEventEdits = async (
  form: EditEventFormState,
  event: SpaceEvent,
  api: SaveApi,
): Promise<SaveOutcome> => {
  const errors = validateEditForm(form);
  if (hasEditErrors(errors)) {
    return { status: "validation", errors };
  }

  const diff = diffAgainst(form, event);
  if (!diff.event && diff.sessions.length === 0) {
    return { status: "no-changes" };
  }

  if (diff.event) {
    await api.updateEvent(diff.event);
  }

  const rejectedSessions: Record<string, string> = {};
  for (const change of diff.sessions) {
    try {
      // Sequential on purpose: per-session errors map cleanly to cards.
      await api.updateSession(change);
    } catch (error) {
      rejectedSessions[change.sessionId] =
        error instanceof SpacesApiError
          ? error.detail.message
          : "Saving this session failed. Please try again.";
    }
  }

  return Object.keys(rejectedSessions).length > 0
    ? { status: "rejected", rejectedSessions }
    : { status: "saved" };
};
